/** Boot: wires storage, HUD and the game loop together. */

import './styles.css';

import { Game } from './game/game';
import type { Phase } from './game/game';
import { bestFor, load, recordBest, resetProgress, save } from './game/storage';
import type { SaveData } from './game/storage';
import { resolveTheme } from './game/theme';
import type { ThemeName, ThemePreference } from './game/theme';
import { detectLang } from './i18n';
import type { Lang } from './i18n';
import { formatTime } from './ui/format';
import { Hud } from './ui/hud';
import type { OverlayKind } from './ui/hud';

const THEME_CYCLE: readonly ThemePreference[] = ['auto', 'dark', 'light'];
const THEME_COLOR: Record<ThemeName, string> = { dark: '#05060b', light: '#f4f5fa' };

function requireElement<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`Missing element #${id}`);
  return node as T;
}

const state: SaveData = load();
let lang: Lang = state.lang ?? detectLang();

const board = requireElement('board');
const canvas = requireElement<HTMLCanvasElement>('canvas');

const hud = new Hud({
  // Exhaustive on purpose. This was a chain of `if` with an unguarded tail that
  // advanced a level, so adding an overlay kind and forgetting a branch silently
  // skipped one — which is exactly what happened when the picker was added. The
  // `never` assignment makes the next omission a compile error instead.
  onOverlayAction: (kind: OverlayKind) => {
    switch (kind) {
      case 'intro':
        state.introSeen = true;
        save(state);
        hud.hideOverlay();
        board.focus({ preventScroll: true });
        return;

      case 'failed':
        game.restart();
        board.focus({ preventScroll: true });
        return;

      case 'levels':
        // Dismissing the picker is just dismissing it.
        hud.hideOverlay();
        board.focus({ preventScroll: true });
        return;

      case 'solved':
        goToLevel(game.currentLevel + 1);
        return;

      default: {
        const unhandled: never = kind;
        throw new Error(`Unhandled overlay kind: ${String(unhandled)}`);
      }
    }
  },
  onOverlaySecondary: (kind: OverlayKind) => {
    switch (kind) {
      case 'solved':
        // Replays the level just solved, so the best time is chaseable.
        game.restart();
        board.focus({ preventScroll: true });
        return;

      case 'levels':
        // The only irreversible action in the game, sitting next to the progress
        // it erases. The HUD asks once before this lands.
        resetProgress(state);
        save(state);
        hud.hideOverlay();
        goToLevel(1);
        hud.announce('resetDone');
        return;

      // The intro and failure screens carry no secondary action.
      case 'intro':
      case 'failed':
        return;

      default: {
        const unhandled: never = kind;
        throw new Error(`Unhandled overlay kind: ${String(unhandled)}`);
      }
    }
  },
  onOpenLevels: () => {
    hud.showOverlay('levels', {
      current: game.currentLevel,
      unlocked: state.level,
      best: state.best,
    });
  },
  onPickLevel: (level) => {
    hud.hideOverlay();
    goToLevel(level);
  },
  onToggleLang: () => {
    lang = lang === 'pl' ? 'en' : 'pl';
    state.lang = lang;
    save(state);
    hud.setLang(lang);
  },
  onCycleTheme: () => {
    const next = THEME_CYCLE[(THEME_CYCLE.indexOf(state.theme) + 1) % THEME_CYCLE.length];
    state.theme = next;
    save(state);
    applyTheme();
  },
  onHelp: () => {
    hud.showOverlay('intro');
  },
});

const game = new Game({
  canvas,
  surface: board,
  hooks: {
    onPhase: handlePhase,
    onProgress: (remaining, total) => {
      hud.setProgress(remaining, total);
    },
    onElapsed: (ms) => {
      hud.setTime(ms);
    },
  },
});

function handlePhase(phase: Phase): void {
  switch (phase) {
    case 'drawing':
      hud.hideOverlay();
      hud.announce('a11yStart');
      return;

    case 'idle':
      hud.hideOverlay();
      hud.setTime(0);
      // Covers every route back to a clean board — the R shortcut, a retry, a
      // replay, a level change — so the reset is never silent for assistive tech.
      hud.announce('a11yRestarted', { level: game.currentLevel });
      return;

    case 'failed':
      hud.showOverlay('failed');
      hud.announce('a11yFailed');
      return;

    case 'solved': {
      const level = game.currentLevel;
      const elapsed = game.elapsedMs;
      const isNewBest = recordBest(state, level, elapsed);
      state.level = Math.max(state.level, level + 1);
      save(state);

      hud.setTime(elapsed);
      hud.setBest(bestFor(state, level));
      hud.showOverlay('solved', { timeMs: elapsed, isNewBest });
      hud.announce('a11ySolved', { time: formatTime(elapsed, 2) });
      return;
    }
  }
}

function goToLevel(level: number): void {
  game.loadLevel(level);
  hud.setLevel(level);
  hud.setBest(bestFor(state, level));
  board.focus({ preventScroll: true });
}

function applyTheme(): void {
  const resolved = resolveTheme(state.theme);
  document.documentElement.dataset.theme = resolved;
  requireElement<HTMLMetaElement>('meta-theme-color').content = THEME_COLOR[resolved];
  game.setTheme(resolved);
  hud.setThemePreference(state.theme);
}

if (typeof matchMedia === 'function') {
  matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
    if (state.theme === 'auto') applyTheme();
  });
}

hud.setLang(lang);
applyTheme();
goToLevel(state.level);
game.start();

if (!state.introSeen) {
  hud.showOverlay('intro');
} else {
  board.focus({ preventScroll: true });
}

if (import.meta.env.DEV) {
  // Console handle for driving a real board while debugging. The guard is
  // statically false in a production build, so this is dropped at bundle time.
  (globalThis as unknown as Record<string, unknown>).loopline = { game, hud, state };
}
